import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Typography } from '@mui/material';
import { IoChevronDownOutline } from 'react-icons/io5';
import Button from 'components/Common/Button';
import Select from 'components/Common/Select';
import Input from 'components/Common/Input';
import Radio from 'components/Common/Radio';
import { MdDelete } from 'react-icons/md';
import { FaPlus } from 'react-icons/fa6';
import Grid from '@mui/material/Grid2';
import { useFieldArray, useWatch } from 'react-hook-form';
import PasswordInput from './PasswordInput';
import { useEffect, useState } from 'react';
import TimePicker from './TimePicker';
import Switch from './Switch';

interface FieldConfig {
  key: string;
  required: boolean;
  multiple: boolean;
  type:
    | 'text'
    | 'number'
    | 'select'
    | 'array'
    | 'textarea'
    | 'email'
    | 'password'
    | 'url'
    | 'time'
    | 'radio'
    | 'boolean';
  default?: boolean;
  label?: string;
  description?: string;
  placeholder?: string;
  options?: { value: any; label: any }[];
  group?: FieldConfig[];
  unique?: boolean;
  auto_submit?: boolean;
}

interface DynamicFormRendererProps {
  fields: FieldConfig[];
  control: any;
  namePrefix?: string;
  gridSize?: number;
  spacing?: string | number;
  submitForm?: any;
  disabled?: boolean;
  publicForm?: boolean;
}

const ArrayField = ({
  fieldConfig,
  control,
  namePrefix = '',
  disabled = false,
  submitForm,
  publicForm
}: {
  fieldConfig: FieldConfig;
  control: any;
  namePrefix?: string;
  disabled?: boolean;
  submitForm?: any;
  publicForm?: boolean;
}) => {
  const fieldName = namePrefix ? `${namePrefix}.${fieldConfig.key}` : fieldConfig.key;
  const { fields, append, remove } = useFieldArray({ control, name: fieldName });
  const values = useWatch({ control, name: fieldName }) || [];
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (values?.length > 0) setExpanded(true);
  }, [values.length]);

  const formatLabel = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

  const addField = () => {
    const newItem =
      fieldConfig.group?.reduce((acc: any, item) => {
        if (item.multiple) {
          acc[item.key] = [];
        } else if (item.default !== undefined) {
          acc[item.key] = item.default;
        } else {
          acc[item.key] = '';
        }
        return acc;
      }, {}) || {};
    append(newItem);
    setExpanded(true);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disabled={disabled}
      sx={{
        boxShadow: 'none',
        overflow: 'auto',
        border: '1px solid #F3F3F3',
        '::before': { background: 'transparent' }
      }}
    >
      <AccordionSummary
        expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
        aria-controls={`${fieldConfig.key}-content`}
        id={`${fieldConfig.key}-header`}
      >
        <Typography variant='h6' fontSize={16} fontWeight={600}>
          {fieldConfig.label || formatLabel(fieldConfig.key)}
          {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        {fields.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              mb: 3
            }}
          >
            {fieldConfig.group?.map((groupItem) => {
              let updatedField = groupItem;

              // filter unique select options
              if (groupItem.type === 'select' && groupItem.unique) {
                const selectedValues = values
                  .map((v: any, i: number) => {
                    if (i === index) return null;
                    const val = v?.[groupItem.key];
                    return groupItem.multiple && Array.isArray(val) ? val : val ? [val] : [];
                  })
                  .flat()
                  .filter(Boolean);

                updatedField = {
                  ...groupItem,
                  options: (groupItem.options || []).filter((opt) => !selectedValues.includes(opt.value))
                };
              }

              return (
                <Box key={groupItem.key} flex={1}>
                  <FormSchemaParser
                    fields={[updatedField]}
                    control={control}
                    namePrefix={`${fieldName}[${index}]`}
                    publicForm={publicForm}
                    gridSize={12}
                    disabled={disabled}
                    submitForm={submitForm}
                  />
                </Box>
              );
            })}

            {!disabled && (
              <IconButton color='error' onClick={() => remove(index)}>
                <MdDelete />
              </IconButton>
            )}
          </Box>
        ))}

        {!disabled && (
          <Button
            type='button'
            color='secondary'
            size='small'
            variant='outlined'
            startIcon={<FaPlus />}
            onClick={addField}
            sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
          >
            Add {fieldConfig.label || formatLabel(fieldConfig.key)}
          </Button>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const SingleField = ({
  fieldConfig,
  control,
  namePrefix = '',
  submitForm,
  disabled = false,
  publicForm = false
}: {
  fieldConfig: FieldConfig;
  control: any;
  namePrefix?: string;
  submitForm?: any;
  disabled?: boolean;
  publicForm?: boolean;
}) => {
  const fieldName = namePrefix ? `${namePrefix}.${fieldConfig.key}` : fieldConfig.key;

  const formatLabel = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
  const label = fieldConfig.label || formatLabel(fieldConfig.key);

  const getInputType = (type: string) => {
    switch (type) {
      case 'email':
        return 'email';
      case 'password':
        return 'password';
      case 'url':
        return 'url';
      case 'number':
        return 'number';
      default:
        return 'text';
    }
  };

  switch (fieldConfig.type) {
    case 'select':
      return (
        <Box>
          <Select
            name={fieldName}
            label={label}
            control={control}
            options={fieldConfig.options || []}
            helperText={fieldConfig.description}
            multiple={fieldConfig.multiple}
            sx={{ minWidth: '7rem' }}
            required={fieldConfig.required && !publicForm}
            {...(fieldConfig.auto_submit
              ? {
                  onChange: (value: any) => {
                    submitForm(value);
                  }
                }
              : {})}
            fullWidth
            disabled={disabled}
          />
        </Box>
      );

    case 'textarea':
      return (
        <Input
          name={fieldName}
          label={label}
          control={control}
          placeholder={fieldConfig.placeholder}
          required={fieldConfig.required && !publicForm}
          helperText={fieldConfig.description}
          fullWidth
          multiline
          rows={4}
          disabled={disabled}
        />
      );

    case 'password':
      return (
        <PasswordInput
          name={fieldName}
          label={label}
          control={control}
          placeholder={fieldConfig.placeholder}
          required={fieldConfig.required && !publicForm}
          helperText={fieldConfig.description}
          fullWidth
          disabled={disabled}
        />
      );

    case 'time':
      return (
        <Box mb={2}>
          <TimePicker name={fieldName} control={control} label={label} disabled={disabled} />
        </Box>
      );

    case 'radio':
      return (
        <>
          <Typography fontSize={14}>{label}</Typography>
          <Radio
            name={fieldName}
            control={control}
            options={fieldConfig.options || []}
            row
            sx={
              disabled
                ? {
                    pointerEvents: 'none',
                    opacity: 0.5
                  }
                : {}
            }
          />
        </>
      );

    case 'boolean':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography fontSize={14} fontWeight={500}>
                {label}
              </Typography>
              {fieldConfig.description ? (
                <Typography fontSize={12} color='text.secondary' sx={{ pr: 6 }}>
                  {fieldConfig.description}
                </Typography>
              ) : null}
            </Box>
            <Switch name={fieldName} control={control} disabled={disabled} />
          </Box>
        </Box>
      );

    default:
      return (
        <Input
          name={fieldName}
          label={label}
          control={control}
          type={getInputType(fieldConfig.type)}
          placeholder={fieldConfig.placeholder}
          required={fieldConfig.required && !publicForm}
          helperText={fieldConfig.description}
          fullWidth
          disabled={disabled}
        />
      );
  }
};

const FormSchemaParser = ({
  fields,
  control,
  namePrefix = '',
  gridSize = 12,
  spacing = 2,
  submitForm,
  disabled = false,
  publicForm = false
}: DynamicFormRendererProps) => {
  return (
    <Grid container spacing={spacing}>
      {fields.map((fieldConfig: FieldConfig, index: number) => (
        <Grid size={gridSize} key={`${namePrefix}-${fieldConfig.key}-${index}`}>
          {fieldConfig.type === 'array' ? (
            <ArrayField
              fieldConfig={fieldConfig}
              publicForm={publicForm}
              control={control}
              namePrefix={namePrefix}
              submitForm={submitForm}
              disabled={disabled}
            />
          ) : (
            <SingleField
              fieldConfig={fieldConfig}
              publicForm={publicForm}
              control={control}
              namePrefix={namePrefix}
              submitForm={submitForm}
              disabled={disabled}
            />
          )}
        </Grid>
      ))}
    </Grid>
  );
};

export default FormSchemaParser;
